<template>
  <div v-if="showMap">
    <LabelledInput>
      <b-checkbox v-model="enableSearchByLocation">
        Enable filter projects by location <HelpfulTooltip text="Allows the user to filter projects based on an address. Addresses are restricted by map bounds. Adjust the map bounds under map settings if you are getting unusual address results." />
      </b-checkbox>
    </LabelledInput>

    <template v-if="enableSearchByLocation && !exclude.includes('settings')">
      <LabelledInput>
        <b-checkbox
          v-model="showExample"
          switch
        >
          Show sample location search <HelpfulTooltip text="Turns on a sample location so you can see the effect of searching by filter. This setting is not saved." />
        </b-checkbox>
      </LabelledInput>

      <LabelledInput
        label="Mini-map settings"
        helpful-tip="Customise the mini-map to show address location"
      >
        <BasemapConfig
          :basemap-type.sync="basemapType"
          :basemap-default.sync="basemapDefault"
          :custom-url.sync="customUrl"
          :custom-attribution.sync="customAttribution"
        />
      </LabelledInput>
    </template>
  </div>
</template>

<script lang="ts">

import Vue, { PropType } from 'vue'
import Component from 'vue-class-component'

import BasemapConfig from '@/components/builder/MapSettings/BasemapConfig.vue'
import LabelledInput from '@/components/builder/UI/LabelledInput.vue'
import HelpfulTooltip from '@/components/builder/UI/HelpfulTooltip.vue'
import { CurrentLocation } from '@/store/public/search'
import { SEARCH_CONFIG_KEY } from '@/constants'
import { LeafNode } from '@/types'

const components = {
  BasemapConfig,
  LabelledInput,
  HelpfulTooltip,
}

const props = {
  exclude: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
}

// eslint-disable-next-line no-use-before-define
@Component<FilterByLocation>({
  components,
  watch: {
    enableSearchByLocation (newValue) {
      // enableSearch toggles on the search button etc
      // Must leave enabled if enableSearchForProjects=true
      this.enableSearch = newValue || this.enableSearchForProjects
      if (!newValue) {
        this.$typedStore.public.search.resetState()
      }
    },
    displayPlaceholder (display) {
      if (display) this.showPlaceholder()
    },
    showExample (display) {
      if (display) this.showPlaceholder()
      else this.$typedStore.public.search.resetFilterByLocation()
    },
  },
})
export default class FilterByLocation extends Vue.extend({ props }) {
  showExample = false

  mounted () {
    if (this.displayPlaceholder) this.showPlaceholder()
  }

  beforeDestroy () {
    if ('placeholder' in this.location && this.location.placeholder) {
      this.$typedStore.public.search.resetFilterByLocation()
    }
  }

  get enableSearch () {
    return this.$typedStore.primary.app.config[SEARCH_CONFIG_KEY].enabled
  }

  set enableSearch (value) {
    this.$typedStore.primary.app.updateConfig({
      configPath: [SEARCH_CONFIG_KEY, 'enabled'],
      value,
    })
  }

  get enableSearchByLocation () {
    return this.$typedStore.primary.app.config[SEARCH_CONFIG_KEY].enableSearchByLocation
  }

  set enableSearchByLocation (value) {
    this.$typedStore.primary.app.updateConfig({
      configPath: [SEARCH_CONFIG_KEY, 'enableSearchByLocation'],
      value,
    })
  }

  get enableSearchForProjects () {
    return this.$typedStore.primary.app.config[SEARCH_CONFIG_KEY].enableSearchForProjects
  }

  set enableSearchForProjects (value) {
    this.$typedStore.primary.app.updateConfig({
      configPath: [SEARCH_CONFIG_KEY, 'enableSearchForProjects'],
      value,
    })
  }

  get basemapType () {
    return this.$typedStore.primary.app.config[SEARCH_CONFIG_KEY].basemapType
  }

  set basemapType (value) {
    this.$typedStore.primary.app.updateConfig({
      configPath: [SEARCH_CONFIG_KEY, 'basemapType'],
      value,
    })
  }

  get basemapDefault () {
    return this.$typedStore.primary.app.config[SEARCH_CONFIG_KEY].basemapDefault
  }

  set basemapDefault (value) {
    this.$typedStore.primary.app.updateConfig({
      configPath: [SEARCH_CONFIG_KEY, 'basemapDefault'],
      value,
    })
  }

  get customUrl () {
    return this.$typedStore.primary.app.config[SEARCH_CONFIG_KEY].basemapCustom.url
  }

  set customUrl (value) {
    this.$typedStore.primary.app.updateConfig({
      configPath: [SEARCH_CONFIG_KEY, 'basemapCustom', 'url'],
      value,
    })
  }

  get customAttribution () {
    return this.$typedStore.primary.app.config[SEARCH_CONFIG_KEY].basemapCustom.attribution
  }

  set customAttribution (value) {
    this.$typedStore.primary.app.updateConfig({
      configPath: [SEARCH_CONFIG_KEY, 'basemapCustom', 'attribution'],
      value,
    })
  }

  get location () {
    return this.$typedStore.public.search.currentLocation
  }

  set location (newValue) {
    this.$typedStore.public.search.setCurrentLocation(newValue)
  }

  get allRecords () {
    return this.$typedStore.visualisationData.leafDataForRoot.map((node: LeafNode) => node.recordId)
  }

  get thirdRecords () {
    return this.allRecords.slice(0, this.allRecords.length * (2 / 3))
  }

  get displayPlaceholder () {
    return this.showExample && this.enableSearch && this.enableSearchByLocation && this.isEmpty(this.location) && this.showMap
  }

  get showMap () {
    return this.$typedStore.visualisationApp.showMap
  }

  isEmpty (value: CurrentLocation) {
    return this.$typedStore.primary.app.isEmpty(value)
  }

  showPlaceholder () {
    this.location = {
      placeholder: true,
      lat: -43.534258,
      lon: 172.640515,
      display_name: '130 Lichfield Street, Christchurch Central, Christchurch (Sample Address)',
    }

    this.$typedStore.public.search.setRecordsToShow(this.thirdRecords)
    this.$typedStore.public.search.setFilterRecords(true)
  }
}

</script>
